"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Loader, Building2, User, Mail, Phone, Calendar, MessageSquare } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const inputClasses = "flex h-12 w-full rounded-lg border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200";
function CorporateInquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-12 text-center space-y-6 bg-card/30 backdrop-blur-md border border-border/50 rounded-xl animate-in fade-in zoom-in duration-500">
        <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center">
          <Building2 className="w-10 h-10 text-primary" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-bold text-foreground">Inquiry Received</h3>
          <p className="text-muted-foreground max-w-md mx-auto">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 min-w-[200px]">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="company_name" rules={{
        required: "Company Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-medium">Company Name</FormLabel>
              <FormControl>
                <div className="relative">
                  <Building2 className="absolute left-3 top-3.5 h-5 w-5 text-muted-foreground/50" />
                  <input {...field} className={`${inputClasses} pl-10`} placeholder="Acme Corp." type="text" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="contact_name" rules={{
        required: "Contact Person is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-medium">Contact Person</FormLabel>
              <FormControl>
                <div className="relative">
                  <User className="absolute left-3 top-3.5 h-5 w-5 text-muted-foreground/50" />
                  <input {...field} className={`${inputClasses} pl-10`} placeholder="Jane Doe" type="text" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-medium">Email Address</FormLabel>
              <FormControl>
                <div className="relative">
                  <Mail className="absolute left-3 top-3.5 h-5 w-5 text-muted-foreground/50" />
                  <input {...field} className={`${inputClasses} pl-10`} placeholder="jane.doe@acme.com" type="email" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="phone" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-medium">Phone Number</FormLabel>
              <FormControl>
                <div className="relative">
                  <Phone className="absolute left-3 top-3.5 h-5 w-5 text-muted-foreground/50" />
                  <input {...field} className={`${inputClasses} pl-10`} placeholder="+1 555-123-4567" type="tel" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="lease_term" rules={{
        required: "Please select a lease term"
      }} render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel className="text-foreground/90 font-medium">Preferred Lease Term</FormLabel>
              <FormControl>
                <div className="relative">
                  <SafeHydrate><Calendar className="absolute left-3 top-3.5 h-5 w-5 text-muted-foreground/50" /></SafeHydrate>
                  <select {...field} className={`${inputClasses} pl-10 appearance-none`}>
                    <option value="" disabled>Select a term</option>
                    <option value="Short-term (1-3 months)">Short-term (1-3 months)</option>
                    <option value="Mid-term (3-12 months)">Mid-term (3-12 months)</option>
                    <option value="Long-term (12+ months)">Long-term (12+ months)</option>
                  </select>
                  <div className="absolute right-3 top-4 pointer-events-none">
                    <svg className="h-4 w-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="message" render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel className="text-foreground/90 font-medium">Additional Details <span className="text-muted-foreground font-normal text-xs ml-1">(Optional)</span></FormLabel>
              <FormControl>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3.5 h-5 w-5 text-muted-foreground/50" />
                  <textarea {...field} className={`${inputClasses} pl-10 min-h-[120px] py-3 resize-y`} placeholder="Provide any specific requirements or questions..." />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg animate-in fade-in slide-in-from-top-2">
          <p className="text-destructive text-sm font-medium flex items-center gap-2">
            <span className="text-lg"></span> {submitError}
          </p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-semibold shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all duration-300">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 h-5 w-5" />
            Processing Request...
          </> : "Submit Corporate Inquiry"}
      </Button>
    </Form>;
}
export default function CorporateInquiry() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="corporate-inquiry" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/10 via-background to-background pointer-events-none" />
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-50" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div ref={ref} className={`max-w-4xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="text-center mb-12 space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-b from-foreground to-foreground/70">
              Partner with Ledotravel
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Streamline your business mobility with our tailored corporate solutions. 
              Fill out the form below to discuss your fleet requirements with our experts.
            </p>
          </div>

          <div className="bg-card/40 backdrop-blur-xl border border-border/50 rounded-2xl p-6 md:p-10 shadow-2xl shadow-black/20">
            <FormProvider formId="corporate_inquiry_form_lease_business" sectionName="corporate-inquiry" defaultValues={{
            company_name: "",
            contact_name: "",
            email: "",
            phone: "",
            lease_term: "",
            message: ""
          }} validationRules={{
            company_name: {
              required: true
            },
            contact_name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true
            },
            lease_term: {
              required: true
            }
          }} submitText="Submit Corporate Inquiry" successMessage="Thank you for your interest in Ledotravel Corporate Solutions. Our fleet management team will review your requirements and contact you within 24 hours.">
              <CorporateInquiryForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}